import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import { ProtectedRoute, Layout } from '@/components';
import {
  Dashboard,
  SiteAnalytics,
  Products,
  AddProduct,
  BulkPriceUpdate,
  Orders,
  InvoiceQueuePage,
  OrderDetail,
  BulkDeleteOrders,
  Customers,
  CustomerDetail,
  Taxonomy,
  Settings,
  NotFound,
  Login,
  VendorManagement,
  Debug,
  VendorForm,
  CouriersList,
  CourierDetail,
  AccountingDashboard,
  AccountingOrders,
  UnauthorizedPage,
  RBACManagement,
  AdminUsersList,
  DeliveryAreas,
  VendorOrdersPage,
  VendorOrderDetailPage,
  VendorCouriersPage,
  VendorCourierFormPage,
  VendorProfilePage,
  VendorAccountingPage,
  VendorSettlementList,
  VendorSettlementDetail,
  NotificationsPage,
} from '@/pages';
import { SettlementList, SettlementDetail } from '@/pages/Settlements';
import { PlatformExpenseList } from '@/pages/PlatformExpenses';
import { ExtraProductsList, ExtraProductForm } from '@/pages/ExtraProducts';
import { CouponsList, CouponForm } from '@/pages/Coupons';
import { SpecialDatesList, SpecialDateForm } from '@/pages/SpecialDates';
import { BannersList, BannerForm } from '@/pages/Banners';
import { ReviewsList } from '@/pages/Reviews';
import { PagesList, PageForm } from '@/pages/Pages';
import { PaymentProvidersList, PaymentProviderForm } from '@/pages/PaymentProviders';
import { BankAccountsList, BankAccountForm } from '@/pages/BankAccounts';
import { LiveChatManager } from '@/pages/LiveChat/LiveChatManager';
import DebugAuth from '@/pages/DebugAuth';
import '@/index.css';

export const App: React.FC = () => {
  const { isAuthenticated, isLoading, user, initAuth } = useAuthStore();

  // Firebase Auth state'i başlat (sadece bir kere)
  useEffect(() => {
    console.log('🚀 App mounted, initializing auth...');
    initAuth();
  }, []); // Boş dependency array - sadece mount'ta çalışır

  console.log('📊 App render:', { isLoading, isAuthenticated, userRole: user?.role });

  if (isLoading) {
    console.log('⏳ App: Still loading...');
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  console.log('✅ App: Loading complete, rendering routes...');

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/analytics" element={<SiteAnalytics />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/new" element={<AddProduct />} />
                  <Route path="/products/:id" element={<AddProduct />} />
                  <Route path="/products/:id/edit" element={<AddProduct />} />
                  <Route path="/products/bulk-price" element={<BulkPriceUpdate />} />
                  <Route path="/extra-products" element={<ExtraProductsList />} />
                  <Route path="/extra-products/new" element={<ExtraProductForm />} />
                  <Route path="/extra-products/:id/edit" element={<ExtraProductForm />} />
                  <Route path="/vendors" element={<VendorManagement />} />
                  <Route path="/vendors/new" element={<VendorForm />} />
                  <Route path="/vendors/:id/edit" element={<VendorForm />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/orders/invoices" element={<InvoiceQueuePage />} />
                  <Route path="/orders/bulk-delete" element={<BulkDeleteOrders />} />
                  <Route path="/orders/:id" element={<OrderDetail />} />
                  <Route path="/customers" element={<Customers />} />
                  <Route path="/customers/:id" element={<CustomerDetail />} />
                  <Route path="/couriers" element={<CouriersList />} />
                  <Route path="/couriers/create" element={<CourierDetail />} />
                  <Route path="/couriers/:courierId" element={<CourierDetail />} />
                  <Route path="/accounting" element={<AccountingDashboard />} />
                  <Route path="/accounting/orders" element={<AccountingOrders />} />
                  <Route path="/settlements" element={<SettlementList />} />
                  <Route path="/settlements/:id" element={<SettlementDetail />} />
                  <Route path="/platform-expenses" element={<PlatformExpenseList />} />
                  <Route path="/vendors" element={<VendorManagement />} />
                  <Route path="/taxonomy" element={<Taxonomy />} />
                  <Route path="/delivery-areas" element={<DeliveryAreas />} />
                  <Route path="/coupons" element={<CouponsList />} />
                  <Route path="/coupons/new" element={<CouponForm />} />
                  <Route path="/coupons/:couponId" element={<CouponForm />} />
                  <Route path="/special-dates" element={<SpecialDatesList />} />
                  <Route path="/special-dates/new" element={<SpecialDateForm />} />
                  <Route path="/special-dates/:specialDateId" element={<SpecialDateForm />} />
                  <Route path="/banners" element={<BannersList />} />
                  <Route path="/banners/new" element={<BannerForm />} />
                  <Route path="/banners/:bannerId/edit" element={<BannerForm />} />
                  <Route path="/reviews" element={<ReviewsList />} />
                  <Route path="/pages" element={<PagesList />} />
                  <Route path="/pages/new" element={<PageForm />} />
                  <Route path="/pages/:pageId/edit" element={<PageForm />} />
                  <Route path="/payment-providers" element={<PaymentProvidersList />} />
                  <Route path="/payment-providers/new" element={<PaymentProviderForm />} />
                  <Route path="/payment-providers/:id/edit" element={<PaymentProviderForm />} />
                  <Route path="/bank-accounts" element={<BankAccountsList />} />
                  <Route path="/bank-accounts/new" element={<BankAccountForm />} />
                  <Route path="/bank-accounts/:id/edit" element={<BankAccountForm />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/live-chat" element={<LiveChatManager />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/admin-users" element={<AdminUsersList />} />
                  <Route path="/rbac" element={<RBACManagement />} />
                  <Route path="/rbac-management" element={<RBACManagement />} />
                  <Route path="/debug" element={<Debug />} />
                  <Route path="/debug-auth" element={<DebugAuth />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Vendor Routes */}
        <Route
          path="/vendor/*"
          element={
            user?.role === 'admin' || user?.role === 'super_admin' ? (
              <Navigate to="/admin/orders" replace />
            ) : (
              <ProtectedRoute requiredRole="vendor">
                <Layout>
                  <Routes>
                    <Route index element={<Navigate to="orders" replace />} />
                    <Route path="orders" element={<ProtectedRoute requiredPages={['/vendor/orders']}><VendorOrdersPage /></ProtectedRoute>} />
                    <Route path="orders/:id" element={<ProtectedRoute requiredPages={['/vendor/orders']}><OrderDetail /></ProtectedRoute>} />
                    <Route path="notifications" element={<NotificationsPage />} />
                    <Route path="couriers" element={<ProtectedRoute requiredPages={['/vendor/couriers']}><VendorCouriersPage /></ProtectedRoute>} />
                    <Route path="couriers/new" element={<ProtectedRoute requiredPages={['/vendor/couriers']}><VendorCourierFormPage /></ProtectedRoute>} />
                    <Route path="couriers/:courierId" element={<ProtectedRoute requiredPages={['/vendor/couriers']}><VendorCourierFormPage /></ProtectedRoute>} />
                    <Route path="profile" element={<ProtectedRoute requiredPages={['/vendor/profile']}><VendorProfilePage /></ProtectedRoute>} />
                    <Route path="accounting" element={<VendorAccountingPage />} />
                    <Route path="settlements" element={<VendorSettlementList />} />
                    <Route path="settlements/:id" element={<VendorSettlementDetail />} />
                    <Route path="*" element={<Navigate to="/vendor/orders" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            )
          }
        />

        {/* Root redirect */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              user?.role === 'admin' || user?.role === 'super_admin' ? (
                <Navigate to="/admin" replace />
              ) : (
                <Navigate to="/vendor" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="top-right" />
    </Router>
  );
};

export default App;
